import React, { type ReactNode, useId } from 'react'
import { RenderErrors } from '../components/RenderErrors'

export type EditableBridge = {
  mode: 'read' | 'edit'
  onBlur: (e: unknown) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onCompositionStart?: () => void
  onCompositionEnd?: () => void
}

export type CommonMergeInput<TFieldCtx, TFieldProps, TProFieldProps, TFormItemProps> = {
  fieldCtx: TFieldCtx & {
    name: string | number | symbol
    state: { value: unknown; meta: { errors: Array<string | React.ReactNode> } }
    handleChange: (next: unknown) => void
  }
  editable: EditableBridge
  props: {
    id?: string
    label?: string | null | ReactNode
    placeholder?: string | null | string[]
    layout?: 'horizontal' | 'vertical'
    fieldProps?: TFieldProps
    proFieldProps?: TProFieldProps
    formItemProps?: TFormItemProps
  }
  /**
   * Defaults that a concrete field can provide to influence merging.
   * Used for value/onChange/checked/fileList special cases without overriding consumer props.
   */
  fieldDefaults?: {
    fieldProps?: TFieldProps
    proFieldProps?: TProFieldProps
    formItemProps?: TFormItemProps
  }
}

interface CommonFieldProps {
  onBlur: (e: unknown) => void
  onKeyDown: (e: React.KeyboardEvent) => void
}

interface CommonFieldIdProps {
  id?: string
}

interface CommonProFormItemProps {
  mode: 'read' | 'edit'
}

interface CommonFormItemProps {
  validateStatus?: 'success' | 'warning' | 'error' | 'validating'
  help?: ReactNode
  htmlFor?: string
  layout?: 'horizontal' | 'vertical'
}

export type CommonMerged<TFieldProps, TProFieldProps, TFormItemProps> = {
  placeholder?: string | null | string[]
  fieldProps: TFieldProps
  proFieldProps: TProFieldProps
  formItemProps: TFormItemProps & CommonFormItemProps
}

export function mergeFieldProps<TFieldCtx, TFieldProps, TProFieldProps, TFormItemProps>(
  args: CommonMergeInput<TFieldCtx, TFieldProps, TProFieldProps, TFormItemProps>,
): CommonMerged<TFieldProps, TProFieldProps, TFormItemProps> {
  const { fieldCtx, editable, props, fieldDefaults } = args
  const { fieldProps, proFieldProps, formItemProps } = props

  const errors = fieldCtx.state.meta.errors

  const mergedFormItemProps = {
    ...(fieldDefaults?.formItemProps ?? ({} as TFormItemProps)),
    ...(formItemProps ?? ({} as TFormItemProps)),
    validateStatus: (formItemProps as CommonFormItemProps)?.validateStatus ?? (errors.length > 0 ? 'error' : undefined),
    layout: (formItemProps as CommonFormItemProps)?.layout ?? props.layout,
    help:
      (formItemProps as CommonFormItemProps)?.help ??
      React.createElement(RenderErrors, {
        errors,
      }),
  } as TFormItemProps & {
    validateStatus?: 'success' | 'warning' | 'error' | 'validating'
    help?: React.ReactNode
  }

  const mergedProFieldProps = {
    ...(fieldDefaults?.proFieldProps ?? ({} as TProFieldProps)),
    ...proFieldProps,
    mode:
      (proFieldProps as CommonProFormItemProps)?.mode ??
      (fieldDefaults?.proFieldProps as unknown as CommonProFormItemProps | undefined)?.mode ??
      editable.mode,
  } as TProFieldProps

  // Base wiring always available. Concrete fields may override via fieldDefaults
  const baseFieldProps = {
    value: fieldCtx.state.value,
    onChange: (v: unknown) => fieldCtx.handleChange(v),
    onBlur: editable.onBlur,
    onKeyDown: editable.onKeyDown,
    ...(editable.onCompositionStart ? { onCompositionStart: editable.onCompositionStart } : {}),
    ...(editable.onCompositionEnd ? { onCompositionEnd: editable.onCompositionEnd } : {}),
  }

  // Merge order: base first, then user props so callers can override value/onChange if they intentionally transform UI↔domain.
  // After that, force critical handlers (onBlur/onKeyDown, autoFocus) to our desired values.
  const mergedFieldProps = {
    placeholder: props.placeholder ?? (typeof props.label !== 'object' ? props.label : ''),
    ...baseFieldProps,
    ...(fieldDefaults?.fieldProps ?? ({} as TFieldProps)),
    ...(fieldProps ?? ({} as TFieldProps)),
  } as TFieldProps & Partial<CommonFieldProps>

  return {
    formItemProps: mergedFormItemProps,
    proFieldProps: mergedProFieldProps,
    fieldProps: mergedFieldProps,
  }
}

export const useMergedFieldProps: typeof mergeFieldProps = args => {
  const reactId = useId()
  const fieldProps = args.props.fieldProps as CommonFieldIdProps | undefined
  const formItemProps = args.props.formItemProps as CommonFormItemProps | undefined
  const id =
    fieldProps?.id ?? args.props.id ?? formItemProps?.htmlFor ?? `field-${String(args.fieldCtx.name)}-${reactId}`

  return mergeFieldProps({
    ...args,
    props: {
      ...args.props,
      fieldProps: { ...fieldProps, id },
      formItemProps: { ...formItemProps, htmlFor: id },
    },
  })
}
